import React, { useState } from "react";
import ReactDOM from "react-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { FAST_COLOR } from "../styles/tokens";
import { FAST_CATEGORY_LABELS, FastCategoryKey, PinIcon, ClockIcon } from "./FastCategoryIcons";

const initials = (name?: string) => (name || "?").trim().split(" ").map(n => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

// Temps restant avant expiration — calculé côté client depuis expires_at (jamais recalculé côté
// serveur, voir routes/fasts.ts), juste pour l'affichage : "Encore 38 min" en dessous d'1h, plus
// jamais possible ici de toute façon puisque le FAST plafonne à 60 min.
const remainingLabel = (expiresAt: string): string => {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "Terminé";
  const min = Math.round(ms / 60000);
  return min < 1 ? "Se termine bientôt" : `Encore ${min} min`;
};

// Même logique que fastLocationText dans Explore.tsx (dupliquée ici comme initials/timeLabel
// ci-dessus, plutôt qu'un import croisé pour un si petit composant) : jamais l'adresse exacte
// avant d'avoir rejoint, mais la ville (ou quartier + ville) plutôt qu'un "Ma position actuelle"
// qui ne dit rien à qui doit s'y rendre.
const locationText = (f: any): string =>
  f.venue_id ? (f.location_label ? `${f.place_label} · ${f.location_label}` : f.place_label) : (f.location_label || f.place_label);

// Carte compacte pour le carrousel Home — identité FAST distincte du reste du fil (fond sombre,
// contour doré très fin) plutôt qu'une recoloration de PostCard : c'est la seule carte du Home à
// être sombre, ce qui suffit à la signer sans qu'aucun élément ne devienne "doré". Le contour ne
// doit rester qu'un simple trait — jamais un halo, jamais un remplissage.
export default function FastCard({ fast, onArrived }: { fast: any; onArrived?: (id: string, count: number) => void }) {
  const { user } = useApp();
  const isOwnFast = fast.user_id === user?.id;
  const [joined, setJoined] = useState(false);
  const [count, setCount] = useState(fast.participants_count || 0);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [exactAddress, setExactAddress] = useState(fast.exact_address || null);

  const arrive = async () => {
    if (joined || loading) return;
    setLoading(true);
    const prevCount = count;
    setJoined(true);
    setCount((c: number) => c + 1);
    try {
      const data = await api.post(`/fasts/${fast.id}/arrive`, {});
      if (typeof data?.participants_count === "number") setCount(data.participants_count);
      if (data?.exact_address) setExactAddress(data.exact_address);
      onArrived?.(fast.id, data?.participants_count ?? prevCount + 1);
    } catch {
      setJoined(false);
      setCount(prevCount);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div
      onClick={() => setShowDetail(true)}
      style={{ width: 148, flexShrink: 0, backgroundColor: "#111", borderRadius: 18, overflow: "hidden", border: `1px solid ${FAST_COLOR}66`, boxShadow: "0 2px 12px rgba(0,0,0,0.18)", animation: "oltodo-card-in 250ms ease-out", cursor: "pointer" }}
    >
      {/* Keyframe déjà déclarée par PostCard.tsx (globale une fois injectée), redéclarée ici pour
          que l'animation marche même si aucune PostCard n'est encore montée sur la page. */}
      <style>{`@keyframes oltodo-card-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }`}</style>
      <div style={{ padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "#0a0a0a", border: `1px solid ${FAST_COLOR}55`, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 11, boxSizing: "border-box" }}>
            {fast.organizer_avatar ? <img src={fast.organizer_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(fast.organizer_name)}
          </div>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fast.organizer_name}</span>
        </div>

        <div style={{ fontSize: 12.5, fontWeight: 700, color: FAST_COLOR, marginBottom: 4 }}>
          {FAST_CATEGORY_LABELS[fast.category as FastCategoryKey] || "FAST"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }}>
          <PinIcon color="rgba(255,255,255,0.45)" size={12} /> {locationText(fast)}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, color: "rgba(255,255,255,0.6)", marginBottom: 2 }}>
          <ClockIcon color="rgba(255,255,255,0.45)" size={12} /> {timeLabel(fast.starts_at)} · {remainingLabel(fast.expires_at)}
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 10 }}>
          {count > 0 ? `${count} arrivée${count > 1 ? "s" : ""}` : "Personne encore"}
        </div>

        {isOwnFast ? (
          <div style={{ width: "100%", padding: "9px 0", borderRadius: 20, textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 700 }}>
            Votre FAST{count > 0 ? ` · ${count}` : ""}
          </div>
        ) : (
          <button
            onClick={e => { e.stopPropagation(); arrive(); }}
            disabled={joined || loading}
            style={{
              width: "100%", padding: "9px 0", borderRadius: 20, border: "none",
              backgroundColor: joined ? "rgba(255,255,255,0.1)" : "white",
              color: joined ? FAST_COLOR : "#0a0a0a",
              fontSize: 12.5, fontWeight: 800, cursor: joined ? "default" : "pointer",
            }}
          >
            {joined ? `✓ J'arrive${count > 0 ? ` · ${count}` : ""}` : "J'ARRIVE"}
          </button>
        )}
      </div>
    </div>

    {showDetail && ReactDOM.createPortal(
      <div onClick={() => setShowDetail(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "#111", borderRadius: 20, padding: 22, width: "100%", maxWidth: 360, border: `1px solid ${FAST_COLOR}66`, boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: "#0a0a0a", border: `1px solid ${FAST_COLOR}55`, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14, boxSizing: "border-box" }}>
              {fast.organizer_avatar ? <img src={fast.organizer_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(fast.organizer_name)}
            </div>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fast.organizer_name}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: FAST_COLOR }}>{FAST_CATEGORY_LABELS[fast.category as FastCategoryKey] || "FAST"}</div>
            </div>
          </div>

          {exactAddress ? (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(exactAddress)}`}
              target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: FAST_COLOR, marginBottom: 8, textDecoration: "none", fontWeight: 700 }}
            >
              <PinIcon color={FAST_COLOR} size={14} /> Itinéraire, {exactAddress}
            </a>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "rgba(255,255,255,0.75)", marginBottom: 8 }}>
              <PinIcon color="rgba(255,255,255,0.5)" size={14} /> {locationText(fast)}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "rgba(255,255,255,0.75)", marginBottom: 8 }}>
            <ClockIcon color="rgba(255,255,255,0.5)" size={14} /> {timeLabel(fast.starts_at)} · {remainingLabel(fast.expires_at)}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", marginBottom: fast.message ? 8 : 16 }}>
            {count > 0 ? `${count} personne${count > 1 ? "s" : ""} arrivée${count > 1 ? "s" : ""}` : "Personne n'est encore arrivé"}
          </div>
          {fast.message && (
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", backgroundColor: "rgba(255,255,255,0.06)", borderRadius: 12, padding: "10px 12px", marginBottom: 16, lineHeight: 1.4 }}>
              {fast.message}
            </div>
          )}

          {isOwnFast ? (
            <div style={{ width: "100%", padding: "11px 0", borderRadius: 20, textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: 13, fontWeight: 700 }}>
              C'est votre FAST
            </div>
          ) : (
            <button
              onClick={arrive}
              disabled={joined || loading}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 20, border: "none",
                backgroundColor: joined ? "rgba(255,255,255,0.1)" : "white",
                color: joined ? FAST_COLOR : "#0a0a0a",
                fontSize: 13.5, fontWeight: 800, cursor: joined ? "default" : "pointer",
              }}
            >
              {joined ? `✓ J'arrive${count > 0 ? ` · ${count}` : ""}` : "J'ARRIVE"}
            </button>
          )}
        </div>
      </div>,
      document.body
    )}
    </>
  );
}
